import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, X } from "lucide-react";
import { Reveal, Overline, EASE } from "./anim";

const CLIPS = [
  {
    id: "welcome",
    title: "Welcome from Jenn",
    desc: "Who this is for and why it exists",
    poster: "/assets/founder-editorial.png",
    src: "/assets/clips/welcome.mp4",
  },
  {
    id: "what-we-do",
    title: "What We Do Together",
    desc: "Strategy, analysis, and risk in 20 seconds",
    poster: "/assets/founder-consult.png",
    src: "/assets/clips/what-we-do.mp4",
  },
  {
    id: "get-started",
    title: "How to Get Started",
    desc: "From first call to strategic plan",
    poster: "/assets/founder-editorial.png",
    src: "/assets/clips/get-started.mp4",
  },
];

export default function WatchClips() {
  const [active, setActive] = useState(null);
  const [available, setAvailable] = useState([]);

  useEffect(() => {
    fetch("/assets/clips/manifest.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : []))
      .then((ids) => {
        const map = Object.fromEntries(ids.map((m) => [m.id, m.src]));
        setAvailable(CLIPS.filter((c) => map[c.id]).map((c) => ({ ...c, src: map[c.id] })));
      })
      .catch(() => setAvailable([]));
  }, []);

  if (available.length === 0) return null;

  return (
    <section id="watch" data-testid="watch-section" className="border-t border-white/10 bg-[#08080A]">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Reveal>
              <Overline>Watch Instead</Overline>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-5 max-w-xl text-2xl font-light leading-[1.15] tracking-tight text-[#F9F8F6] sm:text-3xl">
                Prefer to <span className="text-gold-sheen">watch</span>, not read?
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.14}>
            <p className="max-w-sm text-sm font-light leading-relaxed text-[#A1A1AA]">
              Three short clips from Jenn that cover the essentials, for when you want the story without the
              scroll.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-3">
          {available.map((clip, i) => (
            <Reveal key={clip.id} delay={i * 0.1}>
              <button
                data-testid={`watch-card-${i + 1}`}
                onClick={() => setActive(clip)}
                className="group relative block w-full overflow-hidden border border-white/10 text-left transition-[border-color,transform,box-shadow] duration-500 hover:-translate-y-1 hover:border-[#C5A059]/50 hover:shadow-[0_24px_70px_rgba(0,0,0,0.55)]"
                aria-label={`Play video: ${clip.title}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={clip.poster}
                    alt={clip.title}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#08080A]/90 via-[#08080A]/20 to-transparent" />
                  <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#C5A059]/60 bg-[#08080A]/60 text-[#C5A059] backdrop-blur-md transition-transform duration-500 group-hover:scale-110">
                    <Play size={18} fill="currentColor" aria-hidden="true" />
                  </span>
                </div>
                <div className="p-5">
                  <p className="text-sm font-medium text-[#F9F8F6]">{clip.title}</p>
                  <p className="mt-1 text-xs font-light text-[#71717A]">{clip.desc}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="video-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-5 backdrop-blur-md"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={active.title}
            >
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm font-medium tracking-wide text-[#F9F8F6]">{active.title}</p>
                <button
                  data-testid="video-close-button"
                  onClick={() => setActive(null)}
                  aria-label="Close video"
                  className="text-[#A1A1AA] transition-colors hover:text-[#C5A059]"
                >
                  <X size={22} />
                </button>
              </div>
              <div className="overflow-hidden border border-[#C5A059]/30 shadow-[0_0_80px_rgba(197,160,89,0.15)]">
                <video
                  data-testid="video-player"
                  src={active.src}
                  poster={active.poster}
                  controls
                  autoPlay
                  playsInline
                  className="aspect-video w-full bg-black"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
